/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useCallback, useEffect } from 'react';

interface DraggableImageProps {
  imageSrc: string;
  width: number;
  bottom: number;
  right: number;
  opacity: number;
  zIndex: number;
  transform: string;
  name: string;
  onChangeXY: (dx: number, dy: number) => void;
  onMouseDown: () => void;
}

const DraggableImage: React.FC<DraggableImageProps> = ({
  imageSrc,
  width,
  bottom,
  right,
  opacity,
  zIndex,
  transform,
  name,
  onChangeXY,
  onMouseDown,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startDragPos, setStartDragPos] = useState({ x: 0, y: 0 });
  const [accumulatedDelta, setAccumulatedDelta] = useState({ dx: 0, dy: 0 });

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLImageElement>) => {
    e.preventDefault(); // Prevents text selection during drag on some browsers.
    e.stopPropagation(); // Stops the event from propagating to parent elements.
    setIsDragging(true);
    setStartDragPos({ x: e.clientX, y: e.clientY });
    setAccumulatedDelta({ dx: 0, dy: 0 }); // Reset accumulated deltas when drag starts
    onMouseDown(); // tell the parent container that the mouse was clicked, so it can close modals
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (isDragging) {
        e.stopPropagation(); // Stops the event from propagating to parent elements.
        setAccumulatedDelta({
          dx: startDragPos.x - e.clientX,
          dy: startDragPos.y - e.clientY,
        });
      }
    },
    [isDragging, startDragPos]
  );

  const handleMouseUp = useCallback((e: MouseEvent) => {
    e.stopPropagation(); // Stops the event from propagating to parent elements.
    if (isDragging) {
      // Call onChangeXY with the accumulated delta when the user releases the mouse
      onChangeXY(accumulatedDelta.dx, accumulatedDelta.dy);
      setAccumulatedDelta({ dx: 0, dy: 0 }); // Reset accumulated deltas when drag starts
      setIsDragging(false);
    }
  }, [isDragging, accumulatedDelta, onChangeXY]);

  // Add global event listeners for mouse move and up to ensure smooth dragging
  useEffect(() => {
    if (isDragging) {
      window.document.addEventListener('mousemove', handleMouseMove);
      window.document.addEventListener('mouseup', handleMouseUp);
    } else {
      window.document.removeEventListener('mousemove', handleMouseMove);
      window.document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.document.removeEventListener('mousemove', handleMouseMove);
      window.document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!imageSrc) {
    // If there is no image source, don't even show the block, or the 'export to image' feature will fail!
    return null;
  }

  return (
    <div style={{ position: 'relative' }}>
      <img
        id="monster-image"
        src={imageSrc}
        onMouseDown={handleMouseDown}
        style={{
          position: 'absolute',
          width: `${width}px`,
          bottom: `${bottom + accumulatedDelta.dy}px`,
          right: `${right + accumulatedDelta.dx}px`,
          zIndex: zIndex,
          transform: transform,
          cursor: 'grab',
          opacity: opacity,
        }}
        alt={name}
      />
    </div>
  );
};

export default DraggableImage;
